import { pluginConfig } from '@/globals/pluginConfig';
import { openMenu } from '@/views/menu/menu';

interface ParsedShortcut {
    alt: boolean;
    shift: boolean;
    ctrl: boolean;
    code: string; // matches event.code (layout-independent, unaffected by Option/Alt character output)
}

function parseShortcut(shortcut: string): ParsedShortcut {
    const parts = shortcut.toLowerCase().split('+');
    const rawKey = parts[parts.length - 1];
    // Map shortcut key to event.code (lowercased) for layout-independent matching.
    // Alt/Option on Mac changes event.key ("a" → "å") but never changes event.code.
    let code: string;
    if (/^\d$/.test(rawKey)) {
        code = `digit${rawKey}`;        // "1" → "digit1" (event.code "Digit1")
    } else if (/^f\d{1,2}$/.test(rawKey)) {
        code = rawKey;                  // "f1" → "f1"  (event.code "F1")
    } else if (rawKey === 'space') {
        code = 'space';                 // "space" → "space" (event.code "Space")
    } else if (/^[a-z]$/.test(rawKey)) {
        code = `key${rawKey}`;          // "a" → "keya" (event.code "KeyA")
    } else {
        code = rawKey;                  // unknown keys: compare as-is
    }
    return {
        alt:   parts.includes('alt'),
        shift: parts.includes('shift'),
        ctrl:  parts.includes('ctrl'),
        code,
    };
}

function isTypingContext(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    const tag = target.tagName.toLowerCase();
    return ['input', 'textarea', 'select'].includes(tag) ||
           (target.getAttribute('contenteditable') ?? 'false') !== 'false';
}

let registered = false;

export function registerKeyboardShortcut(): void {
    if (registered) return;
    const keyboard = pluginConfig.keyboard;
    if (!keyboard?.enabled || !keyboard.shortcut) return;

    const parsed = parseShortcut(keyboard.shortcut);

    document.addEventListener('keydown', (event: KeyboardEvent) => {
        if (isTypingContext(event.target)) return;
        if (
            event.altKey   === parsed.alt   &&
            event.shiftKey === parsed.shift &&
            event.ctrlKey  === parsed.ctrl  &&
            event.code.toLowerCase() === parsed.code
        ) {
            event.preventDefault();
            openMenu();
        }
    });
    registered = true;
}
